import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type { CreatedSelectFilter } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys } from '../../../../../common/constants/product-keys';
import type { SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getCachedThirdPartyFilterOptions } from '../utils/third-party-filter-options-cache';

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('calendarType');
const calendarTypeProducts: ProductKeys[] = Array.from(
	new Set<ProductKeys>([...salesforceProducts, ...products3PFromConfig]),
);

const fetchDynamicCalendarTypeOptions = async (
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const cloudId = config.cloudId;
	const datasourceId = config.selectedDatasource?.datasourceId;

	if (!cloudId || !datasourceId) {
		return [];
	}

	try {
		const result = await getCachedThirdPartyFilterOptions(config);

		if (!result) {
			return [];
		}

		const eventTypes = result.filters.find((f) => f.name === 'eventTypes');
		if (!eventTypes || eventTypes.values.length === 0) {
			return [];
		}

		return eventTypes.values.map((eventType) => ({
			value: eventType.name,
			trackingKey: eventType.name,
			queryParamValue: eventType.name,
			isDefault: false,
			label: eventType.label ?? eventType.name,
			product: config.product,
			isSelected: false,
			isSuggested: false,
		}));
	} catch (e) {
		return [];
	}
};

const fetchCalendarTypeOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	return fetchDynamicCalendarTypeOptions(config);
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await fetchCalendarTypeOptions(config);
	const selected = new Set(queryParams);
	const result = allOptions.filter((option) => selected.has(option.queryParamValue));
	return result;
};

export const filter: CreatedSelectFilter<'calendarType'> = createMultiSelectFilter({
	id: 'calendarType',
	requiresTopLevel3PDatasource: true,
	products: calendarTypeProducts,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'eventTypes',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return fetchCalendarTypeOptions(config);
		},
	},
});
